/**
 * ProfessionalHeader
 * Header section displaying professional's photo, name, and key info
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage, AvatarLoading } from '@shared/components/ui/avatar';
import { getInitials } from '@shared/lib/utils';
import type { Professional } from '@shared/types';
import { ProfilePictureViewer } from './ProfilePictureViewer';

// ============================================================================
// Types
// ============================================================================

interface ProfessionalHeaderProps {
  professional: Professional;
}

// ============================================================================
// Component
// ============================================================================

export function ProfessionalHeader({ professional }: ProfessionalHeaderProps) {
  const initials = getInitials(professional.professionalName);
  const [imageLoading, setImageLoading] = useState(!!professional.profilePictureUrl);
  const [imageError, setImageError] = useState(false);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const showImage = !!professional.profilePictureUrl && !imageError;
  const isLoading = imageLoading && showImage;

  return (
    <div className='bg-card border-b border-border'>
      <div className='container mx-auto px-2 py-2'>
        <div className='flex flex-row gap-6 items-center'>
          {/* Avatar */}
          <ProfilePictureViewer imageUrl={professional.profilePictureUrl} altText={professional.professionalName}>
            <Avatar className='h-24 w-24 border-4 border-background shadow-lg'>
              <AvatarImage
                src={professional.profilePictureUrl}
                alt={professional.professionalName}
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
              <AvatarFallback className='text-3xl'>{initials}</AvatarFallback>
              <AvatarLoading isLoading={isLoading} />
            </Avatar>
          </ProfilePictureViewer>

          {/* Info section with overflow handling */}
          <div className='flex-1 min-w-0'>
            <h1 className='text-3xl md:text-4xl font-bold break-words overflow-hidden'>
              {professional.professionalName}
            </h1>
            {/* Owner name subtitle - only for claimed profiles */}
            {professional.status !== 'UNCLAIMED' && professional.user?.firstName && (
              <p className='text-lg text-muted-foreground mt-1 truncate'>{professional.user.firstName}</p>
            )}
          </div>
        </div>

        {/* Profession Card */}
        {professional.bio && (
          <Card className='border-none border-t-2 border-border shadow-none'>
            <CardHeader className='pb-3 px-2'>
              <CardTitle className='text-sm font-medium flex items-center gap-2'>
                {/* <PersonStandingIcon size={24} className="text-ring" /> */}
                Sobre mi
              </CardTitle>
            </CardHeader>
            <CardContent className='pb-4 px-2'>
              <div className=''>
                <p className='text-sm'>{professional.bio}</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
